import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, startOfDay, endOfDay } from 'date-fns';
import enAU from 'date-fns/locale/en-AU';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useSpaces } from '../contexts/SpacesContext';
import { getAllConfirmedBookings, getMockBookings } from '../services/bookingService';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_EMAILS = [process.env.REACT_APP_ADMIN_EMAIL, 'jack@thehubcrookwell.com.au'].filter(Boolean);

const locales = { 'en-AU': enAU };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const eventStyleGetter = (event, spaces) => {
  const space = spaces.find((s) => s.id === event.spaceId);
  return {
    style: {
      backgroundColor: space ? space.color : '#94a3b8',
      borderColor: space ? space.color : '#94a3b8',
      color: '#fff',
      borderRadius: '4px',
      fontSize: '12px',
      padding: '1px 4px',
    },
  };
};

const SpaceLegend = ({ space, availCount }) => (
  <div className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white">
    <div className="flex items-center gap-2.5">
      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: space.color }} />
      <div>
        <div className="text-sm font-semibold text-slate-700">{space.name}</div>
        <div className="text-xs text-slate-400">
          {space.type === 'room' ? `Seats ${space.capacity}` : `${space.totalSeats} desks`}
        </div>
      </div>
    </div>
    <div className="text-right">
      <div className="text-xs font-medium" style={{ color: availCount > 0 ? space.color : '#ef4444' }}>
        {availCount > 0 ? `${availCount} available` : 'Fully booked'}
      </div>
      <Link to={`/book?space=${space.id}`} className="text-xs text-brand-500 hover:underline">
        Book →
      </Link>
    </div>
  </div>
);

// Modal shown when user selects a slot on the calendar
const SlotModal = ({ slot, onClose, onBook }) => {
  const [startHour, setStartHour] = useState(slot.start.getHours() || 9);
  const [endHour, setEndHour] = useState(Math.max((slot.end.getHours() || 10), (slot.start.getHours() || 9) + 1));
  const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7am – 7pm

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-lg">Book Meeting Room</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bg-brand-50 border border-brand-100 rounded-xl p-3 text-sm text-brand-800">
          <strong>{format(slot.start, 'EEEE, d MMMM yyyy')}</strong>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Start time</label>
            <select
              className="input-field"
              value={startHour}
              onChange={(e) => {
                const h = +e.target.value;
                setStartHour(h);
                if (endHour <= h) setEndHour(h + 1);
              }}
            >
              {hours.map((h) => (
                <option key={h} value={h}>{format(new Date().setHours(h, 0), 'h:mm a')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">End time</label>
            <select
              className="input-field"
              value={endHour}
              onChange={(e) => setEndHour(+e.target.value)}
            >
              {hours.filter((h) => h > startHour).map((h) => (
                <option key={h} value={h}>{format(new Date().setHours(h, 0), 'h:mm a')}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-400 text-center">
          Duration: {endHour - startHour} hr{endHour - startHour !== 1 ? 's' : ''}
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={() => onBook(slot.start, startHour, endHour)}
            className="btn-primary flex-1"
          >
            Book This Slot →
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AvailabilityPage() {
  const { currentUser, userProfile } = useAuth();
  const spaces = useSpaces();
  const navigate = useNavigate();

  const [view, setView] = useState('week');
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const loadEvents = useCallback(async (rangeDate) => {
    setLoading(true);
    try {
      const start = startOfDay(addDays(rangeDate, -7));
      const end = endOfDay(addDays(rangeDate, 30));
      let bookings;
      try {
        bookings = await getAllConfirmedBookings(start, end);
      } catch {
        bookings = getMockBookings(start, end);
      }
      const calEvents = bookings.map((b) => {
        const space = spaces.find((s) => s.id === b.spaceId);
        const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
        const bEnd = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
        return {
          id: b.id,
          title: b.isManualBlock
            ? (b.memberName ? `${b.memberName}` : 'Reserved')
            : (b.spaceId === 'meeting-room' ? 'Meeting Room Reserved' : 'Desk Reserved'),
          start: bStart,
          end: bEnd,
          resourceId: b.spaceId,
          spaceId: b.spaceId,
          spaceName: space?.name || b.spaceId,
          allDay: b.spaceId !== 'meeting-room',
        };
      });
      setEvents(calEvents);
    } finally {
      setLoading(false);
    }
  }, [spaces]); // eslint-disable-line

  useEffect(() => { loadEvents(date); }, [date, loadEvents]);

  const isAdmin = currentUser && (ADMIN_EMAILS.includes(currentUser.email) || userProfile?.role === 'admin');
  if (!isAdmin) return <Navigate to="/" replace />;

  const resources = spaces.map((s) => ({ resourceId: s.id, resourceTitle: s.name }));

  const handleSelectSlot = (slotInfo) => {
    const now = new Date();
    if (slotInfo.start < now) return; // Don't allow booking in the past
    // Only allow slot booking for meeting room
    // In day view the resourceId is set; in week view assume meeting room
    const resourceId = slotInfo.resourceId || 'meeting-room';
    if (resourceId !== 'meeting-room') return;
    setSelectedSlot(slotInfo);
  };

  const handleBookSlot = (day, startHour, endHour) => {
    const start = new Date(day);
    start.setHours(startHour, 0, 0, 0);
    const end = new Date(day);
    end.setHours(endHour, 0, 0, 0);
    setSelectedSlot(null);
    navigate(`/book?space=meeting-room&pricing=hourly&prestart=${start.getTime()}&preend=${end.getTime()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {selectedSlot && (
        <SlotModal
          slot={selectedSlot}
          onClose={() => setSelectedSlot(null)}
          onBook={handleBookSlot}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-800">Availability</h1>
            <p className="text-slate-500 mt-1">
              Check what's available. Click any open time slot to book the meeting room.
            </p>
          </div>
          <Link to="/book" className="btn-primary self-start sm:self-auto">Book a Space</Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Spaces</h3>
            {spaces.map((space) => {
              const today = format(new Date(), 'yyyy-MM-dd');
              const bookedToday = events.filter(
                (e) => e.spaceId === space.id && format(e.start, 'yyyy-MM-dd') === today
              ).length;
              const avail = (space.totalSeats || 1) - bookedToday;
              return <SpaceLegend key={space.id} space={space} availCount={Math.max(0, avail)} />;
            })}

            <div className="card p-4 mt-2 border-brand-100 bg-brand-50/50">
              <h4 className="font-semibold text-brand-800 text-sm mb-2">📅 Book Meeting Room</h4>
              <p className="text-xs text-brand-700">
                Click any open slot on the calendar to book the meeting room for that time.
              </p>
            </div>

            <div className="card p-4">
              <h4 className="font-semibold text-slate-700 text-sm mb-3">Opening Hours</h4>
              <div className="space-y-1 text-xs text-slate-500">
                <div className="flex justify-between">
                  <span>Mon – Fri</span>
                  <span className="font-medium text-slate-700">8:00am – 6:00pm</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="font-medium text-slate-700">9:00am – 1:00pm</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-medium text-red-500">Closed</span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between">
                  <span className="text-brand-600 font-medium">Members</span>
                  <span className="font-medium text-slate-700">24/7 access</span>
                </div>
              </div>
            </div>

            {selectedEvent && (
              <div className="card p-4 border-brand-200">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-slate-700 text-sm">Booking</h4>
                  <button onClick={() => setSelectedEvent(null)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm font-medium text-slate-700">{selectedEvent.spaceName}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {format(selectedEvent.start, 'EEE d MMM')}
                  {!selectedEvent.allDay && ` · ${format(selectedEvent.start, 'h:mm a')} – ${format(selectedEvent.end, 'h:mm a')}`}
                </p>
              </div>
            )}
          </div>

          {/* Calendar */}
          <div className="lg:col-span-3">
            <div className="card p-4 md:p-6">
              {loading && (
                <div className="flex items-center justify-center h-12 mb-4">
                  <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-slate-500">Loading availability…</span>
                </div>
              )}
              <div className="min-h-[600px]">
                <Calendar
                  localizer={localizer}
                  events={events}
                  resources={view === 'day' ? resources : undefined}
                  resourceIdAccessor="resourceId"
                  resourceTitleAccessor="resourceTitle"
                  startAccessor="start"
                  endAccessor="end"
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  eventPropGetter={(event) => eventStyleGetter(event, spaces)}
                  onSelectEvent={setSelectedEvent}
                  onSelectSlot={handleSelectSlot}
                  min={new Date(0, 0, 0, 7, 0)}
                  max={new Date(0, 0, 0, 20, 0)}
                  views={['month', 'week', 'day']}
                  culture="en-AU"
                  style={{ height: 600 }}
                  popup
                  selectable
                  messages={{
                    today: 'Today',
                    previous: '←',
                    next: '→',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day',
                    noEventsInRange: 'No bookings in this period — all spaces available!',
                  }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-3 text-center">
                Click an open time slot to book the meeting room. Switch to <strong>Day</strong> view to see all spaces side by side.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
